<script setup lang="ts">

  const props = defineProps({
    verifyEmailUrl: {
      type: String,
      required: true
    },
    saveProfileUrl: {
      type: String,
      required: true
    },
    accountUrl: {
      type: String,
      required: true
    },
    trans: {type: Object, required: true},
  });

  import { ref } from 'vue';
  import { getReCaptchaToken } from '../../js/get_recaptcha_token';

  const showSecondStep = ref(false);
  const email = ref('');
  let saving = ref(false);
  const verifyEmailError = ref('');
  const saveProfileError = ref('');

  const clearErrors = () => {
    // Clear error elements
    const errorElements = document.querySelectorAll('.notice-error');
    errorElements.forEach((element) => {
      element.remove();
    });

    // Remove not-valid class from inputs
    const notValidElements = document.querySelectorAll('.not-valid');
    notValidElements.forEach((element) => {
      element.classList.remove('not-valid');
    });

    saveProfileError.value = '';
  };

  const verifyEmail = async () => {
    try {
      saving.value = true;

      const res = await fetch(props.verifyEmailUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.value }),
      });

      if (!res.ok) {
        throw new Error('Email verification failed');
      }

      const data = await res.json();
      if (data.success) {
        showSecondStep.value = true;
      } else {
        verifyEmailError.value = data.message;
      }
    } catch (error) {
      console.error('Error verifying email:', error);
    } finally {
      saving.value = false;
    }
  };

  const saveProfile = async () => {
    try {
      saving.value = false;

      const token = await getReCaptchaToken('new_account_form');

      const formElement = document.querySelector('#form-signup') as HTMLFormElement;
      const formData = new FormData(formElement);
      const formObject = Object.fromEntries(formData.entries());
      formObject.email = email.value;

      const res = await fetch(props.saveProfileUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          'g-recaptcha-response': token,
          'form-data': formObject,
          'type': '',
          'id': ''
        }),
      });

      const data = await res.json();

      if (data.success) {
        clearErrors();

        if (data.data.url !== '') {
          window.location.href = data.data.url;
        } else {
          window.location.href = props.accountUrl;
        }
      } else {
        clearErrors();

        if (data.message) {
          saveProfileError.value = data.message;
        }

        // Iterate data.data.errors
        for (const [key, value] of Object.entries(data.data.errors)) {
          if (value) {
            const input = document.querySelector(`[id="${key}"]`) as HTMLInputElement;
            if (input) {
              // Add error class to input
              input.classList.add('not-valid');
              // Add error message
              const errorElement = document.createElement('div');
              errorElement.classList.add('notice-error');
              errorElement.classList.add('notice');
              if (typeof value === "string") {
                errorElement.innerHTML = value;
              }
              input.parentNode?.appendChild(errorElement);
            }
          }
        }
      }

    } catch (error: any) {
      saveProfileError.value = error.message || 'An error occurred while saving the profile';
    } finally {
      saving.value = false;
    }
  };

  const handleSubmit = (event: Event) => {
    event.preventDefault();

    if (!showSecondStep.value) {
      verifyEmail();
    } else {
      saveProfile();
    }
  };

</script>

<template>
  <form id="form-signup" :class="['form-default', 'form-signup', { 'form--shadow': !showSecondStep, 'form--no-pad': showSecondStep }]"
        action="" method="post" @submit="handleSubmit">
    <!-- Step 1 -->
    <div>
      <div class="form-row">
        <label for="_email">{{ trans.email }}</label>
        <input v-model="email" :disabled="showSecondStep" tabindex="1" type="text" id="_email" name="_email">
      </div>
      <p v-if="!showSecondStep && verifyEmailError" id="verify-email-error" v-html="verifyEmailError"></p>
      <div class="form-row align-items--flex-end">
        <div>
          <button :disabled="saving" v-if="!showSecondStep" id="verify-email" tabindex="4" type="submit" class="btn-solid icon-rarr">{{ trans.next }}</button>
        </div>
      </div>
    </div>

    <!-- Step 2 -->
    <div v-if="showSecondStep">
      <hr>
      <div class="form-row">
        <label for="formOfAddress">{{ trans.form_of_address }}</label>
        <div id="formOfAddress" class="inputs-group">
          <label class="ui-radio">
            <input name="formOfAddress" type="radio" value="1">
            <span>{{ trans.form_of_address_mr }}</span>
          </label>
          <label class="ui-radio">
            <input name="formOfAddress" type="radio" value="2">
            <span>{{ trans.form_of_address_mrs }}</span>
          </label>
        </div>
      </div>
      <div class="form-row">
        <div class="row">
          <div class="col-1-of-2">
            <label for="firstName">{{ trans.first_name }}</label>
            <input id="firstName" type="text" name="firstName" required>
          </div>
          <div class="col-1-of-2">
            <label for="lastName">{{ trans.last_name }}</label>
            <input id="lastName" type="text" name="lastName" required>
          </div>
        </div>
      </div>
      <div class="form-row form-row--extra-wide">
        <div class="row">
          <div class="col-1-of-2">
            <label for="street">{{ trans.street }}</label>
            <input id="street" type="text" name="street" required>
          </div>
          <div class="col-1-of-2">
            <label for="number">{{ trans.house_number }}</label>
            <input id="number" type="text" name="number" required>
          </div>
        </div>
      </div>
      <div class="form-row form-row--wide-r">
        <div class="row">
          <div class="col-1-of-2">
            <label for="town">{{ trans.town }}</label>
            <input id="town" type="text" name="town" required>
          </div>
          <div class="col-1-of-2">
            <label for="postcode">{{ trans.postcode }}</label>
            <input id="postcode" type="text" name="postcode" required>
          </div>
        </div>
      </div>
      <div class="form-row">
        <label for="phone">{{ trans.phone }}</label>
        <input id="phone" type="text" name="phone" required>
      </div>
      <hr>
      <h2>{{ trans.create_password }}</h2>
      <div class="form-row">
        <div class="row">
          <div class="col-1-of-2">
            <label for="password">{{ trans.password }}</label>
            <input id="password" name="password" required type="password">
          </div>
          <div class="col-1-of-2">
            <label for="passwordRepeat">{{ trans.password_repeat }}</label>
            <input id="passwordRepeat" name="passwordRepeat" required type="password">
          </div>
        </div>
      </div>
      <hr>
      <div class="form-row form-row--extra-margin">
        <div class="inputs-group">
          <label class="ui-checkbox" id="newsletterSubscriber">
            <input type="checkbox" name="newsletterSubscriber">
            <span>{{ trans.register_newsletter }}</span>
          </label>
          <label class="ui-checkbox" id="termsAgree">
            <input type="checkbox" name="termsAgree" required>
            <span v-html="trans.agree_terms"></span>
          </label>
        </div>
      </div>
      <div class="notice notice-error" v-if="saveProfileError">{{ saveProfileError }}</div>
      <div class="form-row align-items--flex-end">
        <div>
          <button :disabled="saving" tabindex="4" type="submit" class="btn-solid icon-rarr">{{ trans.create_account }}</button>
        </div>
      </div>
    </div>
  </form>
</template>
